import * as React from 'react';
import {
  Box,
  StackLayout,
  UtilityText,
  GDSHeadingLevel,
  HeadingText,
  ParagraphText,
  GDSHeadingSize,
} from '@leagueplatform/genesis-core';
import { getSubHeadingLevel } from '#utils/get-sub-heading-level.util.ts';
import { AccumulatorCard } from '#components/accumulator-card/accumulator-card.component.tsx';
import type { Accumulator as AccumulatorType } from '../plan-usage-widget.types';

interface TierProps {
  heading?: string;
  headingSize?: GDSHeadingSize;
  headingLevel?: GDSHeadingLevel;
  description?: string;
  accumulators?: AccumulatorType[];
  lastUpdated?: string;
  index: number;
  onTooltipClick?: (tooltipId: string) => void;
}

export function TierComponent({
  heading,
  headingSize = 'sm',
  headingLevel = '4',
  description,
  accumulators,
  lastUpdated,
  index,
  onTooltipClick: onTooltipClickCallback,
}: TierProps) {
  const accumulatorHeadingLevel = React.useMemo(
    () => (heading ? getSubHeadingLevel(headingLevel) : headingLevel),
    [heading, headingLevel],
  );
  const hasHeadingSection = heading || description;

  return (
    <StackLayout spacing="$one" css={{ width: '100%' }}>
      {hasHeadingSection && (
        <StackLayout spacing="$threeQuarters">
          {heading && (
            <HeadingText level={headingLevel} size={headingSize}>
              {heading}
            </HeadingText>
          )}

          {description && (
            <ParagraphText size="sm" color="subdued">
              {description}
            </ParagraphText>
          )}
        </StackLayout>
      )}

      <Box
        as="ul"
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '$oneAndHalf',
          width: '100%',
        }}
      >
        {accumulators?.map((accumulator, accIndex) => (
          <Box
            as="li"
            key={`accumulator-${index}-${
              accumulator.heading?.heading || accIndex
            }`}
          >
            <AccumulatorCard
              eyebrow={accumulator.eyebrow}
              heading={accumulator.heading?.heading}
              headingSize={accumulator.heading?.headingSize}
              headingLevel={accumulatorHeadingLevel}
              tooltip={accumulator.tooltip}
              amountSpent={accumulator.amountSpent}
              totalAmount={accumulator.totalAmount}
              progressValue={accumulator.progressValue}
              amountLeft={accumulator.amountLeft}
              onTooltipClick={onTooltipClickCallback}
            />
          </Box>
        ))}
      </Box>

      {lastUpdated && (
        <UtilityText size="xs" color="subdued">
          {lastUpdated}
        </UtilityText>
      )}
    </StackLayout>
  );
}
